import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Button } from "@/components/Interface/Button/Button";
import { CardModule } from "@/components/Layout/BaseLayouts/CardModule/CardModule";
import { SubscriptionInfoBox } from "@/components/Layout/SubscriptionInfoBox/SubscriptionInfoBox";
import { useAlert } from "@/hooks/useAlert";
import { useSimIdFromQueryParam } from "@/hooks/useSimIdFromQueryParam";
import { startNetworkReset } from "@/services/api";
import { SimCardDetailNetworkResetNotAllowedReasonsItem } from "@/services/model";
import { AlertTypes } from "@/utils/atoms";
import { capitalizeFirstLetter } from "@/utils/translationHelpers";

import { NetworkResetDisabledTooltip } from "./NetworkResetDisabledTooltip/NetworkResetDisabledTooltip";

const recommendationKeys = [
  "wait",
  "remove",
  "reboot",
  "rebootWatchSim",
] as const;

export const NetworkReset = ({
  disabled,
  reasons,
  isActive,
  isBlocked,
  onOpen,
  onClose,
}: {
  disabled?: boolean;
  reasons?: SimCardDetailNetworkResetNotAllowedReasonsItem[];
  isActive: boolean;
  isBlocked: boolean;
  onOpen: () => void;
  onClose: () => void;
}) => {
  const [simId] = useSimIdFromQueryParam();
  const showAlert = useAlert();
  const { t } = useTranslation(undefined, {
    keyPrefix: "cockpit.managementTile.details.sections.details.networkReset",
  });
  const { t: tPortal } = useTranslation("portal");
  const [isResetting, setIsResetting] = useState(false);

  const confirmReset = () => {
    if (isResetting) {
      return;
    }

    setIsResetting(true);
    startNetworkReset(parseInt(simId))
      .then((response) => {
        showAlert({
          type: response.resetAllowed ? AlertTypes.success : AlertTypes.error,
          text: t(response.resetAllowed ? "success" : "error"),
        });
      })
      .catch(() => {
        showAlert({ type: AlertTypes.error, text: t("error") });
      })
      .finally(() => {
        setIsResetting(false);
        onClose();
      });
  };

  return (
    <CardModule
      dataTestid="network-reset"
      stickyHeader={isActive}
      title={isActive ? t("label") : t("title")}
      actions={
        isActive ? (
          <>
            <Button
              dataTestid="network-reset-cancel"
              size="sm"
              variant="secondary"
              disabled={isResetting}
              onClick={onClose}
            >
              {capitalizeFirstLetter(tPortal("common.buttons.cancel"))}
            </Button>
            <Button
              dataTestid="network-reset-confirm"
              size="sm"
              variant="primary"
              loading={isResetting}
              onClick={confirmReset}
            >
              {capitalizeFirstLetter(tPortal("common.buttons.confirm"))}
            </Button>
          </>
        ) : disabled ? (
          <NetworkResetDisabledTooltip reasons={reasons} leftOnMobile>
            <Button
              dataTestid="network-reset-button"
              size="sm"
              variant="primary"
              disabled
            >
              {t("link")}
            </Button>
          </NetworkResetDisabledTooltip>
        ) : (
          <Button
            dataTestid="network-reset-button"
            size="sm"
            variant="primary"
            disabled={isBlocked}
            onClick={onOpen}
          >
            {t("link")}
          </Button>
        )
      }
      bodyGap={2}
    >
      {isActive ? (
        <SubscriptionInfoBox className="flex flex-col gap-2">
          <div className="text-body-xs leading-none text-foreground-secondary">
            <Trans
              t={t}
              components={[<p key="paragraph" className="mt-2" />]}
              i18nKey="confirmationText"
            />
          </div>
          <ul className="list-disc pl-4 text-body-xs leading-none text-foreground-secondary">
            {recommendationKeys.map((key) => (
              <li key={key}>{t(`recommendations.${key}`)}</li>
            ))}
          </ul>
        </SubscriptionInfoBox>
      ) : (
        <SubscriptionInfoBox className="flex flex-col gap-1">
          <p className="text-body-sm font-semibold leading-none text-foreground-primary">
            {t("label")}
          </p>
          <p className="text-body-xs leading-none text-foreground-secondary">
            {t("text")}
          </p>
        </SubscriptionInfoBox>
      )}
    </CardModule>
  );
};
